import { useMemo } from 'react'
import { PATHS } from './body-paths'

interface Injury {
  body_location: string
  grade: string
  healed_at: string | null
}

interface LocationProtection {
  blunt: number
  edge: number
  point: number
  fire: number
}

interface Props {
  injuries: Injury[]
  onZoneClick?: (location: string) => void
  armourProtection?: Record<string, LocationProtection>
  missingLimbs?: Set<string>
}

// Map SVG path IDs to database body_location values
const ZONE_MAP: Record<string, string> = {
  'Head': 'Skull',
  'Face': 'Face',
  'Neck': 'Neck',
  'Left Shoulder': 'Shoulder-L',
  'Right Shoulder': 'Shoulder-R',
  'Left Upper Arm': 'Upper-Arm-L',
  'Right Upper Arm': 'Upper-Arm-R',
  'Left Elbow': 'Elbow-L',
  'Right Elbow': 'Elbow-R',
  'Left Forearm': 'Forearm-L',
  'RightForearm': 'Forearm-R',
  'Left Hand': 'Hand-L',
  'Right Hand': 'Hand-R',
  'Thorax': 'Thorax',
  'Abdoman': 'Abdomen',
  'Groin': 'Groin',
  'Left Hip': 'Hip-L',
  'Right Hip': 'Hip-R',
  'Left Thigh': 'Thigh-L',
  'Right Thigh': 'Thigh-R',
  'Left Knee': 'Knee-L',
  'Right Knee': 'Knee-R',
  'Left Lower Leg': 'Calf-L',
  'Right Lower Leg': 'Calf-R',
  'Left Foot': 'Foot-L',
  'Right Foot': 'Foot-R',
}

// Centroid positions for each SVG zone (x, y in viewBox 0 0 501 978)
// Derived from SVG path coordinate ranges
const ZONE_CENTERS: Record<string, [number, number]> = {
  'Head': [240, 50],
  'Face': [240, 90],
  'Neck': [240, 142],
  'Right Shoulder': [135, 190],
  'Left Shoulder': [310, 200],
  'Right Upper Arm': [125, 260],
  'Left Upper Arm': [360, 270],
  'Right Elbow': [112, 325],
  'Left Elbow': [388, 325],
  'Right Forearm': [100, 390],
  'RightForearm': [100, 390],
  'Left Forearm': [400, 390],
  'Right Hand': [88, 455],
  'Left Hand': [412, 455],
  'Thorax': [240, 240],
  'Abdoman': [240, 358],
  'Groin': [235, 515],
  'Right Hip': [155, 468],
  'Left Hip': [298, 468],
  'Right Thigh': [178, 565],
  'Left Thigh': [270, 565],
  'Right Knee': [182, 665],
  'Left Knee': [257, 665],
  'Right Lower Leg': [180, 770],
  'Left Lower Leg': [260, 770],
  'Right Foot': [168, 880],
  'Left Foot': [262, 880],
}

// SVG path IDs that belong to each missing-limb condition
const MISSING_LIMB_ZONES: Record<string, string[]> = {
  missing_right_arm: ['Right Upper Arm', 'Right Elbow', 'RightForearm', 'Right Hand'],
  missing_left_arm: ['Left Upper Arm', 'Left Elbow', 'Left Forearm', 'Left Hand'],
  missing_right_leg: ['Right Thigh', 'Right Knee', 'Right Lower Leg', 'Right Foot'],
  missing_left_leg: ['Left Thigh', 'Left Knee', 'Left Lower Leg', 'Left Foot'],
}

/** Build a Set of SVG path IDs that should render as missing */
export function buildMissingZones(conditions: string[]): Set<string> {
  const zones = new Set<string>()
  for (const c of conditions) {
    const parts = MISSING_LIMB_ZONES[c]
    if (parts) for (const p of parts) zones.add(p)
  }
  return zones
}

const GRADE_SEVERITY: Record<string, number> = {
  M1: 1, S2: 2, S3: 3, G4: 4, G5: 5, K4: 4, K5: 5,
}

const SEVERITY_COLORS: Record<number, string> = {
  1: '#f59e0b', // amber — minor
  2: '#f97316', // orange — serious
  3: '#ef4444', // red — serious
  4: '#dc2626', // dark red — grievous
  5: '#7f1d1d', // very dark red — grievous/kill
}

export function BodyMap({ injuries, onZoneClick, armourProtection, missingLimbs }: Props) {
  // Build a map of location → worst active injury grade
  const injuryMap = useMemo(() => {
    const map: Record<string, number> = {}
    for (const inj of injuries) {
      if (inj.healed_at) continue
      const severity = GRADE_SEVERITY[inj.grade] ?? 0
      const current = map[inj.body_location] ?? 0
      if (severity > current) map[inj.body_location] = severity
    }
    return map
  }, [injuries])

  const getFill = (svgId: string): string => {
    const location = ZONE_MAP[svgId]
    if (!location) return 'transparent'
    // Injury color takes priority
    const severity = injuryMap[location]
    if (severity) return SEVERITY_COLORS[severity] ?? 'transparent'
    // Armour tint — light blue for protected zones
    const prot = armourProtection?.[location]
    if (prot && (prot.blunt + prot.edge + prot.point) > 0) return '#3b82f6'
    return 'transparent'
  }

  const getOpacity = (svgId: string): number => {
    const location = ZONE_MAP[svgId]
    if (!location) return 0
    if (injuryMap[location]) return 0.5
    // Armour opacity scales with total protection (subtle for cloth, stronger for plate)
    const prot = armourProtection?.[location]
    if (prot) {
      const total = prot.blunt + prot.edge + prot.point
      if (total > 0) return Math.min(0.35, 0.05 + total * 0.01)
    }
    return 0
  }

  const handleClick = (svgId: string) => {
    const location = ZONE_MAP[svgId]
    if (location && onZoneClick) onZoneClick(location)
  }

  const getProtLabel = (svgId: string): string | null => {
    const location = ZONE_MAP[svgId]
    if (!location || !armourProtection) return null
    const p = armourProtection[location]
    if (!p || (p.blunt + p.edge + p.point) === 0) return null
    return `${p.blunt}/${p.edge}/${p.point}`
  }

  return (
    <svg
      xmlns="http://www.w3.org/2000/svg"
      viewBox="0 0 501 978"
      className="w-full max-w-[200px] mx-auto"
    >
      {PATHS.map(({ id, d }) => {
        const isMissing = missingLimbs?.has(id) ?? false
        if (isMissing) {
          return (
            <path
              key={id}
              d={d}
              fill="none"
              stroke="#9c8e78"
              strokeWidth={1}
              strokeDasharray="6 4"
              opacity={0.25}
            >
              <title>{ZONE_MAP[id] ?? id} (missing)</title>
            </path>
          )
        }
        return (
          <path
            key={id}
            d={d}
            fill={getFill(id)}
            fillOpacity={getOpacity(id)}
            stroke={injuryMap[ZONE_MAP[id]] ? SEVERITY_COLORS[injuryMap[ZONE_MAP[id]]] : '#9c8e78'}
            strokeWidth={injuryMap[ZONE_MAP[id]] ? 2 : 1}
            className="cursor-pointer hover:fill-[rgba(139,69,19,0.15)] transition-colors"
            onClick={() => handleClick(id)}
          >
            <title>{ZONE_MAP[id] ?? id}{(() => {
              const p = armourProtection?.[ZONE_MAP[id]]
              return p ? ` — B${p.blunt} E${p.edge} P${p.point} F${p.fire}` : ''
            })()}</title>
          </path>
        )
      })}
      {/* Armour protection labels */}
      {PATHS.map(({ id }) => {
        if (missingLimbs?.has(id)) return null
        const label = getProtLabel(id)
        const center = ZONE_CENTERS[id]
        if (!label || !center) return null
        return (
          <text
            key={`prot-${id}`}
            x={center[0]}
            y={center[1]}
            textAnchor="middle"
            dominantBaseline="central"
            className="pointer-events-none select-none"
            fill={injuryMap[ZONE_MAP[id]] ? '#fff' : '#1e40af'}
            fontSize={32}
            fontWeight="bold"
            opacity={0.85}
          >
            {label}
          </text>
        )
      })}
    </svg>
  )
}
